// @flow

import { isEqual } from 'lodash';
import { useState } from 'react';

import useDeepEqualEffect from '../useDeepEqualEffect';

type InputEvent = SyntheticInputEvent<*>;

type Props<FormState> = {
  defaultState: $Shape<FormState>,
  onSubmit: (state: $Shape<FormState>) => void,
  onValidate?: (state: $Shape<FormState>) => {},
};

type Output<FormState> = {
  errors: $Shape<FormState>,
  handleChange: (value: any, name: $Keys<FormState>) => void,
  handleChangeEvent: (event: InputEvent) => void,
  handleSubmit: (event: SyntheticInputEvent<HTMLFormElement>) => void,
  isDirty: boolean,
  dirty: $Shape<FormState>,
  state: FormState,
};

function noop() {
  return {};
}

function useForm<FormState: { [string]: any }>({
  defaultState = {},
  onSubmit,
  onValidate = noop,
}: Props<FormState>): Output<FormState> {
  const [cachedDefaultState, setCachedDefaultState] = useState(defaultState);
  const [state, setState] = useState<$Shape<FormState>>(defaultState);
  const [errors, setErrors] = useState<$Shape<FormState>>({});
  const [dirty, setDirty] = useState<$Shape<FormState>>({});

  const isDirty = Object.values(dirty).some(k => k);

  // Reset our state if default state has changed
  useDeepEqualEffect(() => {
    setState(defaultState);
    setCachedDefaultState(defaultState);
  }, [defaultState]);

  const handleSubmit = (event: SyntheticInputEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate form
    const validationErrors = onValidate(state);
    setErrors(validationErrors);

    // Only continue if validation passed
    if (Object.entries(validationErrors).length === 0) {
      setDirty({});
      onSubmit(state);
    }
  };

  const handleChange = (value: $Values<FormState>, name: $Keys<FormState>) => {
    // Calculate dirty state
    setDirty((currentDirty: FormState) => ({
      ...currentDirty,
      [name]: value !== (defaultState[name] || ''),
    }));

    // Calculate next state
    setState((currentState: FormState) => ({
      ...currentState,
      [name]: value,
    }));
  };

  const handleChangeEvent = (event: InputEvent) => {
    event.persist();
    const { checked, name, type, value } = event.target;
    // use checked if the input is a checkbox
    handleChange(type === 'checkbox' ? checked : value, name);
  };

  return {
    errors,
    handleChange,
    handleChangeEvent,
    handleSubmit,
    isDirty,
    dirty,
    // If the defaultState is different than what was originally passed in,
    // we will reset the state to the new defaultState value. However, this
    // is a side-effect and we don't want an in-between render cycle where
    // state is incorrect. For instance, if you have a graphql query resolve
    // and `isLoading` changes from `true` to `false`, you would be returning the
    // old state right before the new state is set and returned. This would
    // cause you to have incorrect state values and likely a render cycle
    // where those undefined values get passed to UI components.
    // $FlowFixMe defaultState can be {} because it's a partial of state.
    state: isEqual(defaultState, cachedDefaultState) ? state : defaultState,
  };
}

export default useForm;
